import { useMemo, useState } from 'react';
import {
  usePublishVersion,
  useReportTemplate,
  useReportTemplateVersions,
  useRollback,
  useUpdateTemplateSettings,
} from '../../api/report-templates.js';
import { useCqsPreview } from '../../api/datasets.js';
import type { CqsQuery } from '@canopy/query-spec';
import type { ReportTemplateDefinition, ReportTemplateWithCurrent } from '../../api/types.js';
import { RenderPanel } from './RenderPanel.js';
import { SchedulePanel } from './SchedulePanel.js';
import { AiDesignerPanel } from './AiDesignerPanel.js';

interface Props {
  templateId: string;
  /** Auto-collapse the host page's list sidebar when a render kicks
   *  off — clicking Run is a strong "give me the report" signal. */
  onRenderStart?: () => void;
}

type Tab = 'design' | 'preview' | 'layout' | 'versions' | 'render' | 'schedules' | 'settings';

export function TemplateDetail({ templateId, onRenderStart }: Props): JSX.Element {
  const { data, isLoading, error } = useReportTemplate(templateId);
  const [tab, setTab] = useState<Tab>('design');

  if (isLoading) return <div className="card card-body">Loading template…</div>;
  if (error || !data) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="alert alert--danger">Template unavailable.</div>
        </div>
      </div>
    );
  }
  const currentVersion = data.currentVersion;
  if (!currentVersion) {
    return (
      <div className="card">
        <div className="card-body">
          <h2>{data.name}</h2>
          <p className="text-dim">No published version yet.</p>
        </div>
      </div>
    );
  }

  return (
    <article className="card">
      <header
        className="card-header"
        style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--space-3)' }}
      >
        <div className="row row--between" style={{ width: '100%' }}>
          <div>
            <h2>{data.name}</h2>
            <div className="row" style={{ gap: 'var(--space-2)', marginTop: 4, flexWrap: 'wrap' }}>
              <span className="badge badge--accent">v{currentVersion.version}</span>
              <span className={`badge badge--audience badge--audience-${data.audience}`}>
                {audienceLabel(data.audience)}
              </span>
              <span className="text-dim text-sm">
                Dataset <code>{currentVersion.definition.datasetId}</code>
              </span>
              <span className="text-mute text-xs">·</span>
              <span className="text-mute text-xs">
                {data.pageSize.toUpperCase()} · {data.pageOrientation}
              </span>
              <span className="text-mute text-xs">·</span>
              <span className="text-mute text-xs">
                Published {new Date(currentVersion.publishedAt).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="tabs">
        <Tab active={tab === 'design'} onClick={() => setTab('design')}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M8 2v3M8 11v3M2 8h3M11 8h3M4 4l2 2M10 10l2 2M4 12l2-2M10 6l2-2" />
            </svg>
            Design
          </span>
        </Tab>
        <Tab active={tab === 'preview'} onClick={() => setTab('preview')}>
          Preview
        </Tab>
        <Tab active={tab === 'layout'} onClick={() => setTab('layout')}>
          Layout
        </Tab>
        <Tab active={tab === 'render'} onClick={() => setTab('render')}>
          Render
        </Tab>
        <Tab active={tab === 'schedules'} onClick={() => setTab('schedules')}>
          Schedules
        </Tab>
        <Tab active={tab === 'versions'} onClick={() => setTab('versions')}>
          Versions
        </Tab>
        <Tab active={tab === 'settings'} onClick={() => setTab('settings')}>
          Settings
        </Tab>
      </div>

      {tab === 'design' && (
        <AiDesignerPanel
          templateId={templateId}
          definition={currentVersion.definition}
          currentVersion={currentVersion.version}
        />
      )}
      {tab === 'preview' && <PreviewTab definition={currentVersion.definition} />}
      {tab === 'layout' && (
        <LayoutTab
          templateId={templateId}
          definition={currentVersion.definition}
          currentVersion={currentVersion.version}
        />
      )}
      {tab === 'render' && (
        <RenderPanel
          templateId={templateId}
          templateName={data.name}
          {...(onRenderStart ? { onRenderStart } : {})}
        />
      )}
      {tab === 'schedules' && <SchedulePanel templateId={templateId} />}
      {tab === 'versions' && (
        <VersionsTab templateId={templateId} currentVersionId={currentVersion.id} />
      )}
      {tab === 'settings' && <TemplateSettingsTab templateId={templateId} current={data} />}
    </article>
  );
}

function audienceLabel(a: 'internal' | 'customer' | 'partner' | 'regulator'): string {
  switch (a) {
    case 'internal':
      return 'Internal';
    case 'customer':
      return 'Customer';
    case 'partner':
      return 'Partner';
    case 'regulator':
      return 'Regulator';
  }
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick(): void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button type="button" className={'tab' + (active ? ' tab--active' : '')} onClick={onClick}>
      {children}
    </button>
  );
}

// ─── Preview tab ──────────────────────────────────────────────────

function PreviewTab({ definition }: { definition: ReportTemplateDefinition }): JSX.Element {
  // HTML-layout templates have no tabular column list to build a CQS
  // select from — the Design tab handles their live preview via the
  // /preview/html endpoint. Here we steer the user there.
  if (definition.layout.kind === 'html') {
    return (
      <div className="card-body">
        <div className="alert alert--info">
          <div>
            <strong>This is an HTML template.</strong>
            <div className="text-sm" style={{ marginTop: 6 }}>
              Open the <strong>Design</strong> tab for a live iframe preview against real data, or
              hit <strong>Render</strong> to produce a PDF / Excel / CSV artifact.
            </div>
          </div>
        </div>
      </div>
    );
  }
  const layout = definition.layout;
  const query: CqsQuery = useMemo(
    () => ({
      cqsVersion: '1.0.0',
      datasetId: definition.datasetId,
      select: layout.columns.map((c) => ({ field: c.field })),
      limit: 500,
    }),
    [definition.datasetId, layout.columns],
  );
  const { data, isLoading, error } = useCqsPreview(query);

  if (isLoading) return <div className="card-body text-dim">Running query…</div>;

  if (error) {
    const body = (error as { body?: { code?: string } }).body;
    if (body?.code === 'cqs.dataset_not_accelerated') {
      return (
        <div className="card-body">
          <div className="alert alert--info">
            <div>
              <strong>Dataset is not accelerated yet.</strong>
              <div className="text-sm" style={{ marginTop: 6 }}>
                Preview via CQS requires an accelerated dataset. Head to{' '}
                <a href="/reporting/app/admin/data-sources">Admin → Data Sources</a> to materialize
                it into ClickHouse, or use the <strong>Render</strong> tab to run the raw-proc path
                directly.
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="card-body">
        <div className="alert alert--danger">
          <div>
            <strong>Preview failed</strong>
            <pre>{JSON.stringify(body ?? String(error), null, 2)}</pre>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return <div className="card-body">No data.</div>;

  return (
    <>
      <div className="stat-strip">
        <div className="stat">
          <div className="stat-label">Rows</div>
          <div className="stat-value">{data.rowCount.toLocaleString()}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Backend</div>
          <div className="stat-value stat-value--mono">{data.backend}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Latency</div>
          <div className="stat-value">{data.durationMs} ms</div>
        </div>
        <div className="stat">
          <div className="stat-label">Columns</div>
          <div className="stat-value">{layout.columns.length}</div>
        </div>
      </div>
      <div className="report-preview">
        <h3>{layout.title}</h3>
        <table className="report-grid">
          <thead>
            <tr>
              {layout.columns.map((c) => (
                <th key={c.field} style={{ textAlign: c.align }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, i) => (
              <tr key={i}>
                {layout.columns.map((c) => (
                  <td key={c.field} style={{ textAlign: c.align }}>
                    {formatCell(row[c.field])}
                  </td>
                ))}
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr>
                <td colSpan={layout.columns.length} className="text-center text-mute">
                  No rows matched.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return v.toLocaleString();
  return String(v);
}

function describeLayout(layout: ReportTemplateDefinition['layout']): string {
  if (layout.kind === 'tabular') return `${layout.columns.length} columns (tabular)`;
  const chars = layout.html.length + (layout.css?.length ?? 0);
  return `HTML · ${chars.toLocaleString()} chars`;
}

// ─── Layout tab ──────────────────────────────────────────────────

function LayoutTab({
  templateId,
  definition,
  currentVersion,
}: {
  templateId: string;
  definition: ReportTemplateDefinition;
  currentVersion: number;
}): JSX.Element {
  // HTML-layout templates are authored on the Design tab. We'd expose
  // raw HTML/CSS here too but the Design tab does that better.
  if (definition.layout.kind === 'html') {
    return (
      <div className="card-body">
        <div className="alert alert--info">
          <div>
            <strong>This template is authored as HTML.</strong>
            <div className="text-sm" style={{ marginTop: 6 }}>
              Switch to the <strong>Design</strong> tab — Claude can iterate on it, and the HTML /
              CSS editors are available as fallback panes there.
            </div>
          </div>
        </div>
      </div>
    );
  }
  const tabularDef = definition;
  const tabularLayout = definition.layout;
  const [draft, setDraft] = useState<ReportTemplateDefinition & { layout: typeof tabularLayout }>({
    ...tabularDef,
    layout: tabularLayout,
  });
  const publish = usePublishVersion(templateId);
  const dirty = JSON.stringify(draft) !== JSON.stringify(definition);

  function updateColumn(
    idx: number,
    patch: Partial<(typeof tabularLayout)['columns'][number]>,
  ): void {
    setDraft((d) => ({
      ...d,
      layout: {
        ...d.layout,
        columns: d.layout.columns.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
      },
    }));
  }
  function removeColumn(idx: number): void {
    setDraft((d) => ({
      ...d,
      layout: { ...d.layout, columns: d.layout.columns.filter((_, i) => i !== idx) },
    }));
  }
  function moveColumn(idx: number, dir: -1 | 1): void {
    setDraft((d) => {
      const cols = [...d.layout.columns];
      const t = idx + dir;
      if (t < 0 || t >= cols.length) return d;
      const a = cols[idx]!;
      const b = cols[t]!;
      cols[idx] = b;
      cols[t] = a;
      return { ...d, layout: { ...d.layout, columns: cols } };
    });
  }

  return (
    <div className="card-body stack-lg">
      <div className="row row--between">
        <div className="field" style={{ maxWidth: 420, flex: 1, marginBottom: 0 }}>
          <label className="field-label">Report title</label>
          <input
            className="input"
            value={draft.layout.title}
            onChange={(e) =>
              setDraft((d) => ({ ...d, layout: { ...d.layout, title: e.target.value } }))
            }
          />
        </div>
        <button
          type="button"
          className="btn btn--primary"
          disabled={!dirty || publish.isPending}
          onClick={() => publish.mutate({ definition: draft })}
        >
          {publish.isPending ? 'Publishing…' : `Publish v${currentVersion + 1}`}
        </button>
      </div>

      <div>
        <div className="section-head">
          <div className="section-head-title">
            <h2>Columns</h2>
            <p>Field references, display labels, alignment. Columns are rendered in order.</p>
          </div>
          <span className="text-mute text-sm">{draft.layout.columns.length} columns</span>
        </div>

        <div className="card" style={{ marginBottom: 'var(--space-3)' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Field</th>
                <th>Label</th>
                <th style={{ width: 130 }}>Align</th>
                <th style={{ width: 110 }}></th>
              </tr>
            </thead>
            <tbody>
              {draft.layout.columns.map((c, i) => (
                <tr key={i}>
                  <td className="text-mute cell-mono">{i + 1}</td>
                  <td>
                    <input
                      className="input input--sm input--mono"
                      value={c.field}
                      onChange={(e) => updateColumn(i, { field: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="input input--sm"
                      value={c.label}
                      onChange={(e) => updateColumn(i, { label: e.target.value })}
                    />
                  </td>
                  <td>
                    <select
                      className="select input--sm"
                      value={c.align}
                      onChange={(e) =>
                        updateColumn(i, { align: e.target.value as 'left' | 'center' | 'right' })
                      }
                    >
                      <option value="left">left</option>
                      <option value="center">center</option>
                      <option value="right">right</option>
                    </select>
                  </td>
                  <td>
                    <div className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => moveColumn(i, -1)}
                        aria-label="Move up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => moveColumn(i, 1)}
                        aria-label="Move down"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => removeColumn(i)}
                        aria-label="Remove"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button
          type="button"
          className="btn btn--secondary"
          onClick={() =>
            setDraft((d) => ({
              ...d,
              layout: {
                ...d.layout,
                columns: [
                  ...d.layout.columns,
                  { field: 'new_column', label: 'New column', align: 'left' },
                ],
              },
            }))
          }
        >
          + Add column
        </button>

        {publish.error && (
          <div className="alert alert--danger" style={{ marginTop: 'var(--space-3)' }}>
            <div>
              <strong>Publish failed</strong>
              <pre>
                {JSON.stringify(
                  (publish.error as { body?: unknown }).body ?? String(publish.error),
                  null,
                  2,
                )}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Versions tab ────────────────────────────────────────────────

function VersionsTab({
  templateId,
  currentVersionId,
}: {
  templateId: string;
  currentVersionId: string;
}): JSX.Element {
  const { data, isLoading } = useReportTemplateVersions(templateId);
  const rollback = useRollback(templateId);
  if (isLoading) return <div className="card-body text-dim">Loading versions…</div>;
  if (!data) return <div className="card-body text-mute">No versions.</div>;
  return (
    <div className="card-body">
      <table className="data-table">
        <thead>
          <tr>
            <th>Version</th>
            <th>Title</th>
            <th>Layout</th>
            <th>Published</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.map((v) => {
            const isCurrent = v.id === currentVersionId;
            return (
              <tr key={v.id}>
                <td className="cell-mono">v{v.version}</td>
                <td>{v.definition.layout.title}</td>
                <td className="cell-align-right" style={{ textAlign: 'left' }}>
                  {describeLayout(v.definition.layout)}
                </td>
                <td className="text-dim">{new Date(v.publishedAt).toLocaleString()}</td>
                <td>
                  {isCurrent ? (
                    <span className="badge badge--success">Current</span>
                  ) : (
                    <span className="badge badge--neutral">Archived</span>
                  )}
                </td>
                <td>
                  {!isCurrent && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={rollback.isPending}
                      onClick={() => rollback.mutate({ toVersionId: v.id })}
                    >
                      Roll back
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rollback.error && (
        <div className="alert alert--danger" style={{ marginTop: 'var(--space-3)' }}>
          Rollback failed:{' '}
          <code>
            {JSON.stringify((rollback.error as { body?: unknown }).body ?? String(rollback.error))}
          </code>
        </div>
      )}
    </div>
  );
}

// ─── Settings tab ────────────────────────────────────────────────────

function TemplateSettingsTab({
  templateId,
  current,
}: {
  templateId: string;
  current: ReportTemplateWithCurrent;
}): JSX.Element {
  const update = useUpdateTemplateSettings(templateId);

  const [name, setName] = useState(current.name);
  const [audience, setAudience] = useState(current.audience);
  const [orientation, setOrientation] = useState(current.pageOrientation);
  const [size, setSize] = useState(current.pageSize);
  const [top, setTop] = useState(current.pageMargins.top);
  const [right, setRight] = useState(current.pageMargins.right);
  const [bottom, setBottom] = useState(current.pageMargins.bottom);
  const [left, setLeft] = useState(current.pageMargins.left);
  const dirty =
    name !== current.name ||
    audience !== current.audience ||
    orientation !== current.pageOrientation ||
    size !== current.pageSize ||
    top !== current.pageMargins.top ||
    right !== current.pageMargins.right ||
    bottom !== current.pageMargins.bottom ||
    left !== current.pageMargins.left;

  function save(): void {
    const patch: Parameters<typeof update.mutate>[0] = {};
    if (name !== current.name) patch.name = name;
    if (audience !== current.audience) patch.audience = audience;
    if (orientation !== current.pageOrientation) patch.pageOrientation = orientation;
    if (size !== current.pageSize) patch.pageSize = size;
    if (
      top !== current.pageMargins.top ||
      right !== current.pageMargins.right ||
      bottom !== current.pageMargins.bottom ||
      left !== current.pageMargins.left
    ) {
      patch.pageMargins = { top, right, bottom, left };
    }
    update.mutate(patch);
  }

  return (
    <div className="card-body stack-lg" style={{ maxWidth: 720 }}>
      <section className="stack">
        <h3>Template name</h3>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </section>

      <section className="stack">
        <h3>Audience</h3>
        <p className="text-dim text-sm" style={{ margin: 0 }}>
          Drives branding overlays (header / footer / watermark). Customer and regulator audiences
          may require an elevated role to publish.
        </p>
        <div className="audience-options">
          {(['internal', 'customer', 'partner', 'regulator'] as const).map((a) => (
            <label
              key={a}
              className={'audience-option' + (audience === a ? ' audience-option--on' : '')}
            >
              <input
                type="radio"
                name="audience"
                value={a}
                checked={audience === a}
                onChange={() => setAudience(a)}
              />
              <div>
                <div className="audience-option-title">
                  {a.charAt(0).toUpperCase() + a.slice(1)}
                </div>
                <div className="audience-option-desc">{audienceDescription(a)}</div>
              </div>
            </label>
          ))}
        </div>
      </section>

      <section className="stack">
        <h3>Page options</h3>
        <div className="grid-2">
          <label className="field">
            <span className="field-label">Orientation</span>
            <select
              className="select"
              value={orientation}
              onChange={(e) => setOrientation(e.target.value as 'portrait' | 'landscape')}
            >
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
            </select>
          </label>
          <label className="field">
            <span className="field-label">Paper size</span>
            <select
              className="select"
              value={size}
              onChange={(e) => setSize(e.target.value as 'letter' | 'legal' | 'a4' | 'tabloid')}
            >
              <option value="letter">Letter (8.5 × 11")</option>
              <option value="legal">Legal (8.5 × 14")</option>
              <option value="a4">A4 (210 × 297 mm)</option>
              <option value="tabloid">Tabloid (11 × 17")</option>
            </select>
          </label>
        </div>
        <div className="grid-2" style={{ marginTop: 12 }}>
          <MarginInput label="Top (in)" value={top} onChange={setTop} />
          <MarginInput label="Right (in)" value={right} onChange={setRight} />
          <MarginInput label="Bottom (in)" value={bottom} onChange={setBottom} />
          <MarginInput label="Left (in)" value={left} onChange={setLeft} />
        </div>
      </section>

      <div className="row" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn btn--primary"
          onClick={save}
          disabled={!dirty || update.isPending}
        >
          {update.isPending ? 'Saving…' : 'Save settings'}
        </button>
        {update.isSuccess && !dirty && <span className="text-dim text-sm">✓ Saved</span>}
        {update.error && (
          <span className="text-sm" style={{ color: 'var(--color-danger)' }}>
            Save failed
          </span>
        )}
      </div>
    </div>
  );
}

function MarginInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange(v: number): void;
}): JSX.Element {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <input
        type="number"
        className="input"
        min={0}
        max={3}
        step={0.05}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function audienceDescription(a: 'internal' | 'customer' | 'partner' | 'regulator'): string {
  switch (a) {
    case 'internal':
      return 'No external branding. Lightest footer; no review gate.';
    case 'customer':
      return 'Brand header + confidentiality footer. Requires ReportAdmin to publish.';
    case 'partner':
      return 'Branded header with NDA footer. Requires ReportAuthor+.';
    case 'regulator':
      return 'Fixed regulatory cover; DRAFT watermark until attested. Requires ReportAdmin.';
  }
}
